import React, { useEffect } from 'react';
import { StoreProvider, useStore } from './store/StoreProvider.js';
import { useTheme } from './constants/index.js';
import { AppNav, Footer } from './components/layout.js';
import { ApplyModal } from './components/modals.js';
import { Toast } from './components/ui.js';
import { HomeScreen, FeedScreen, DetailScreen } from './screens/browse.js';
import { AuthScreen, SearchScreen, SavedScreen, AlertsScreen, UnsubscribeAlertScreen } from './screens/user.js';
import { ApplicationsScreen } from './screens/applications.js';
import { AccountSettingsSection } from './screens/AccountSettingsSection.js';
import { EmployersScreen, PostOpportunityScreen } from './screens/employer.js';
import { AboutScreen } from './screens/about.js';
import { AdminReviewScreen } from './screens/admin.js';
import { CommunityScreen } from './screens/community.js';
import { ResourcesScreen, GuideDetailScreen } from './screens/resources.js';
import { SupportPageScreen } from './screens/support.js';

function PageRouter() {
  const s = useStore();

  useEffect(() => {
    const handleRouteChange = () => {
      const params = new URLSearchParams(window.location.search);
      const mode = params.get('mode');
      const path = window.location.pathname;

      if (path.includes('auth')) {
        s.go(mode === 'signup' ? 'signup' : 'login');
      } else if (path === '/' || path === '') {
        s.go('home');
      } else if (path === '/feed') {
        s.go('feed');
      } else if (path === '/search') {
        s.go('search');
      } else if (path === '/saved') {
        s.go('saved');
      } else if (path === '/applications') {
        s.go('applications');
      } else if (path === '/employers') {
        s.go('employers');
      } else if (path === '/post-job') {
        s.go('postJob');
      } else if (path.startsWith('/opportunity/')) {
        const id = path.split('/').pop();
        if (id) s.go('detail', { id });
      } else if (path === '/about') {
        s.go('about');
      } else if (path === '/account') {
        s.go('account');
      } else if (path === '/admin') {
        s.go('admin');
      } else if (path === '/community') {
        s.go('community');
      } else if (path === '/resources') {
        s.go('resources');
      } else if (path.startsWith('/resources/')) {
        const slug = path.split('/').filter(Boolean)[1];
        if (slug) s.go('guideDetail', { slug });
      } else if (path === '/help' || path === '/contact' || path === '/terms' || path === '/privacy' || path === '/guidelines') {
        s.go('support', { supportSlug: path.slice(1) });
      } else if (path === '/alerts') {
        s.go('notifications');
      } else if (path.startsWith('/alerts/unsubscribe')) {
        // public unsubscribe screen
      } else if (path === '/company-profile') {
        s.go('employers', { tab: 'profile' });
      }
    };

    window.addEventListener('popstate', handleRouteChange);
    handleRouteChange();
    return () => window.removeEventListener('popstate', handleRouteChange);
  }, []);

  if (window.location.pathname.includes('auth')) {
    const params = new URLSearchParams(window.location.search);
    const mode = params.get('mode') || 'login';
    return React.createElement(AuthScreen, { mode });
  }

  if (window.location.pathname.startsWith('/alerts/unsubscribe')) {
    return React.createElement(UnsubscribeAlertScreen, null);
  }

  switch (s.route.name) {
    case 'home': return React.createElement(HomeScreen, null);
    case 'feed': return React.createElement(FeedScreen, null);
    case 'search': return React.createElement(SearchScreen, null);
    case 'saved': return React.createElement(SavedScreen, null);
    case 'applications': return React.createElement(ApplicationsScreen, null);
    case 'notifications': return React.createElement(AlertsScreen, null);
    case 'account': return React.createElement(AccountSettingsSection, null);
    case 'employers': return React.createElement(EmployersScreen, null);
    case 'postJob': return React.createElement(PostOpportunityScreen, null);
    case 'detail': return React.createElement(DetailScreen, null);
    case 'login': return React.createElement(AuthScreen, { mode: 'login' });
    case 'signup': return React.createElement(AuthScreen, { mode: 'signup' });
    case 'about': return React.createElement(AboutScreen, null);
    case 'admin': return React.createElement(AdminReviewScreen, null);
    case 'community': return React.createElement(CommunityScreen, null);
    case 'resources': return React.createElement(ResourcesScreen, null);
    case 'guideDetail': return React.createElement(GuideDetailScreen, null);
    case 'support': return React.createElement(SupportPageScreen, null);
    case 'companyProfile':
      return React.createElement(EmployersScreen, null);
    default: return React.createElement(HomeScreen, null);
  }
}

function AppShell() {
  const s = useStore();
  const t = useTheme(s.theme);

  useEffect(() => {
    const handlePopState = () => {
      const path = window.location.pathname;
      const params = new URLSearchParams(window.location.search);
      const mode = params.get('mode');

      if (path.includes('auth')) {
        s.go(mode === 'signup' ? 'signup' : 'login');
      } else if (path === '/' || path === '') {
        s.go('home');
      } else if (path === '/feed') {
        s.go('feed');
      } else if (path === '/search') {
        s.go('search');
      } else if (path === '/saved') {
        s.go('saved');
      } else if (path === '/applications') {
        s.go('applications');
      } else if (path === '/employers') {
        s.go('employers');
      } else if (path === '/post-job') {
        s.go('postJob');
      } else if (path.startsWith('/opportunity/')) {
        const id = path.split('/').pop();
        if (id) s.go('detail', { id });
      } else if (path === '/about') {
        s.go('about');
      } else if (path === '/account') {
        s.go('account');
      } else if (path === '/admin') {
        s.go('admin');
      } else if (path === '/community') {
        s.go('community');
      } else if (path === '/resources') {
        s.go('resources');
      } else if (path.startsWith('/resources/')) {
        const slug = path.split('/').filter(Boolean)[1];
        if (slug) s.go('guideDetail', { slug });
      } else if (path === '/help' || path === '/contact' || path === '/terms' || path === '/privacy' || path === '/guidelines') {
        s.go('support', { supportSlug: path.slice(1) });
      } else if (path === '/alerts') {
        s.go('notifications');
      } else if (path.startsWith('/alerts/unsubscribe')) {
        // handled by PageRouter
      } else if (path === '/company-profile') {
        s.go('employers', { tab: 'profile' });
      }
    };

    window.addEventListener('popstate', handlePopState);
    handlePopState();
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  return React.createElement('div', {
    style: {
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      background: t.bg.page,
      transition: 'background .2s ease',
    },
  },
    React.createElement(AppNav, null),
    React.createElement('main', { style: { flex: 1 } },
      React.createElement(PageRouter, null)
    ),
    React.createElement(Footer, null),
    React.createElement(ApplyModal, null),
    React.createElement(Toast, null)
  );
}

export default function App() {
  return React.createElement(StoreProvider, null,
    React.createElement(AppShell, null)
  );
}
